/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    ISignInB2BAccount,
    ISignInB2BItem,
    ISignInItem,
    ISignInLoading,
    ISignInLocalAccount,
    ISignInSocialAccount,
    ISignInSocialItem,
    ISignInViewProps
} from '@msdyn365-commerce-modules/account-management';

export interface ICustomSignUp {
    label: string;
    link: ICreateAnAccountLinkData;
}

import { ICreateAnAccountLinkData, ISignInProps, ISignInResources } from '../definition-extensions/sign-in.ext.props.autogenerated';

const LocalAccount: React.FC<ISignInLocalAccount> = ({ localAccount, items, forgetPassword, disclaimer, error, signInButton }) => {
    return (
        <Node {...localAccount}>
            {items &&
                items.map((item: ISignInItem) => {
                    return (
                        <Node key={item.key} {...item.wrapper}>
                            {item.label}
                            {item.error}
                            {item.input}
                        </Node>
                    );
                })}
            <Node className='ms-sign-in__account-item-signin'>
                {forgetPassword}
                {signInButton}
            </Node>
            {disclaimer}
            {error}
        </Node>
    );
};

const SocialAccount: React.FC<ISignInSocialAccount> = ({ socialAccounts, items }) => {
    return (
        <Node {...socialAccounts}>
            {items &&
                items.map((item: ISignInSocialItem) => {
                    return (
                        <Node key={item.key} {...item.button}>
                            {item.image}
                            {item.text}
                        </Node>
                    );
                })}
        </Node>
    );
};

const B2BAccount: React.FC<ISignInB2BAccount> = ({ socialAccounts, items }) => {
    return (
        <Node {...socialAccounts}>
            {items &&
                items.map((item: ISignInB2BItem) => {
                    return (
                        item && (
                            <Node key={item.key} {...item.button}>
                                {item.text}
                            </Node>
                        )
                    );
                })}
        </Node>
    );
};

const SignInLoading: React.FC<ISignInLoading> = ({ modal, modalBody, icon, message }) => {
    return (
        <Node {...modal}>
            <Node {...modalBody}>
                {icon}
                {message}
            </Node>
        </Node>
    );
};

const CustomSignUpButton: React.FC<ICustomSignUp> = ({ label, link }) => {
    return (
        <Node className='ms-sign-in__sign-up-link-wrapper'>
            {link && link.linkUrl && link.linkUrl.destinationUrl && (
                <a className='ms-sign-in__sign-up-link msc-btn' href={link.linkUrl.destinationUrl} aria-label={link.ariaLabel}>
                    {label}
                </a>
            )}
        </Node>
    );
};

const SignInView: React.FC<ISignInViewProps & ISignInProps<ISignInResources>> = props => {
    const {
        signIn,
        viewState,
        loading,
        defaultAADConainer,
        aadConainer,
        signInSection,
        signUpSection,
        signInSectionHeading,
        signInLocalAccount,
        signInSocialAccount,
        signUpSectionHeading,
        signInB2BAccount,
        signUpDescription
    } = props;

    return (
        <Module {...signIn}>
            {viewState.isShowLoading && <Node {...defaultAADConainer} />}
            {viewState.isShowLoading && <SignInLoading {...loading} />}
            <Node {...aadConainer}>
                <Node {...signInSection}>
                    {signInSectionHeading}
                    {LocalAccount(signInLocalAccount)}
                </Node>
                <Node {...signUpSection}>
                    {signUpSectionHeading}
                    {signUpDescription}
                    <CustomSignUpButton label={props.config.createAnAccountLabel} link={props.config.createAnAccountLink} />
                </Node>
                {props.config.enableSocialAccountLogin && (
                    <div className='ms-sign-in__additional-section'>
                        <div className='ms-sign-in__additional-section__line-strike'>
                            <span>{props.resources.signUpSeperatorLabel}</span>
                        </div>
                        <h2 className='ms-sign-in__sign-up-heading'>{props.resources.socialAccountSignInHeading}</h2>
                        {SocialAccount(signInSocialAccount)}
                    </div>
                )}
                {props.config.enableEmployeeLogin && (
                    <div className='ms-sign-in__additional-section'>
                        <div className='ms-sign-in__additional-section__line-strike'>
                            <span>{props.resources.signUpSeperatorLabel}</span>
                        </div>
                        <h2 className='ms-sign-in__sign-up-heading'>{props.resources.b2bSocialSignInHeading}</h2>
                        {B2BAccount(signInB2BAccount)}
                    </div>
                )}
            </Node>
        </Module>
    );
};

export default SignInView;
